<template>
  <v-dialog
    v-model="internalValue"
    max-width="650"
    scrollable
    :persistent="loading"
  >
    <v-card data-testid="associations-modal" :data-entity-id="bank && bank.id" class="rounded-lg">
      <!-- Modal Header: Bank identity without avatar icons -->
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <div>
          <div class="text-h6 font-weight-bold">{{ bank ? bank.name : '' }}</div>
          <div class="caption grey--text">
            Associated Brokers
            <span v-if="bank && bank.bankcode"> | Code: {{ bank.bankcode }}</span>
            <span v-if="bank && bank.cnpj"> | CNPJ: {{ bank.cnpj }}</span>
          </div>
        </div>
        <v-btn icon @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" />
      <v-divider v-else />

      <v-card-text class="pa-4" style="max-height: 540px;">
        <!-- Add / Link Broker Section (natural card background) -->
        <v-card outlined class="pa-3 mb-4 rounded-lg">
          <div class="subtitle-2 font-weight-bold mb-2">Link Broker</div>
          <div class="d-flex align-center flex-gap-2">
            <v-select
              data-testid="add-association"
              v-model="selectedBrokerId"
              :items="availableBrokerOptions"
              item-text="displayName"
              item-value="id"
              :item-disabled="(item) => item.disabled"
              label="Select broker to link"
              outlined
              dense
              hide-details
              :disabled="loading"
              class="flex-grow-1 select-broker-dropdown"
            >
              <template v-slot:item="{ item }">
                <div data-testid="association-option" :data-entity-id="item.id" class="d-flex flex-column py-1 w-100 dropdown-item-text">
                  <div class="d-flex align-center">
                    <v-chip
                      x-small
                      outlined
                      :color="item.isPartner ? 'green' : 'primary'"
                      class="mr-2"
                    >
                      {{ item.isPartner ? 'Partner' : 'Broker' }}
                    </v-chip>
                    <span
                      :class="{ 'grey--text': item.disabled }"
                      class="font-weight-medium"
                      style="font-size: 1.05rem;"
                    >
                      {{ item.name }}
                    </span>
                  </div>
                  <div
                    v-if="item.disabledReason"
                    class="caption grey--text text--darken-1 mt-1 pl-1"
                    style="font-size: 0.85rem;"
                  >
                    {{ item.disabledReason }}
                  </div>
                </div>
              </template>

              <!-- Empty dropdown fallback -->
              <template v-slot:no-data>
                <div class="pa-3 text-caption grey--text text-center" style="font-size: 0.95rem;">
                  All brokers are already associated with this bank
                </div>
              </template>
            </v-select>

            <v-btn
              data-testid="link-association"
              color="primary"
              :disabled="!selectedBrokerId || isSelectedDisabled || loading"
              :loading="loadingAction === 'add'"
              @click="handleAddBroker"
            >
              <v-icon left small>mdi-plus</v-icon>
              Link
            </v-btn>
          </div>
        </v-card>

        <!-- Associated Brokers List -->
        <div>
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="subtitle-2 font-weight-bold">
              Associated Brokers ({{ linkedBrokers.length }})
            </span>
          </div>

          <!-- Empty State -->
          <div
            v-if="!linkedBrokers.length"
            class="d-flex flex-column align-center justify-center pa-6 text-center grey--text"
          >
            <v-icon size="40" color="grey darken-1" class="mb-2">mdi-handshake-outline</v-icon>
            <div class="text-body-2 font-weight-medium">No brokers associated with this bank yet</div>
            <div class="caption">Use the dropdown above to link a broker.</div>
          </div>

          <!-- List of Linked Brokers -->
          <v-list v-else dense class="pa-0">
            <v-card
              v-for="broker in linkedBrokers"
              data-testid="associated-broker"
              :data-entity-id="broker.id"
              :key="broker.id"
              outlined
              class="mb-2 pa-3 rounded-lg"
            >
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex flex-column">
                  <div class="d-flex align-center">
                    <span class="font-weight-bold text-body-1 mr-2">{{ broker.name }}</span>
                    <v-chip
                      x-small
                      outlined
                      :color="broker.is_partner ? 'green' : 'primary'"
                    >
                      {{ broker.is_partner ? 'Partner' : 'Broker' }}
                    </v-chip>
                  </div>

                  <div class="caption grey--text mt-1">
                    <span v-if="broker.is_partner">
                      Client of {{ broker.master_name }} | 
                    </span>
                    <span v-else-if="broker.linked_partners_count > 0" class="blue--text text--lighten-3">
                      {{ broker.linked_partners_count }} linked partner{{ broker.linked_partners_count > 1 ? 's' : '' }} | 
                    </span>
                    <span>CNPJ: {{ broker.cnpj || '-' }}</span>
                    <span v-if="broker.email && broker.email !== '-'"> | {{ broker.email }}</span>
                  </div>
                </div>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      :disabled="loading"
                      data-testid="remove-association"
                      @click="requestRemoveBroker(broker)"
                    >
                      <v-icon small color="error">mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Unlink broker</span>
                </v-tooltip>
              </div>
            </v-card>
          </v-list>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3 justify-end">
        <v-btn data-testid="close-associations" text @click="close" :disabled="loading">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirmation Modal for Unlinking a Broker -->
    <delete-modal
      v-model="confirmDeleteDialog"
      :loading="loadingAction === 'remove'"
      :item-label="pendingRemoveBroker ? pendingRemoveBroker.name : ''"
      title="Unlink Broker"
      message="Are you sure you want to unlink this broker from the bank?"
      confirm-text="Unlink"
      @confirm="confirmRemoveBroker"
    >
      <!-- Warn user if removing this broker also unlinks its partners -->
      <div
        v-if="pendingRemoveDependentPartners.length"
        class="mt-2 pa-2 rounded orange darken-4 text-caption white--text"
      >
        <div class="font-weight-bold mb-1">Cascade Warning:</div>
        <div>
          This is a Broker with linked partners. Unlinking it will also automatically unlink its partner
          broker{{ pendingRemoveDependentPartners.length > 1 ? 's' : '' }} from this bank:
        </div>
        <ul class="mt-1 pl-4">
          <li v-for="p in pendingRemoveDependentPartners" :key="p.id">
            {{ p.name }}
          </li>
        </ul>
      </div>
    </delete-modal>
  </v-dialog>
</template>

<script>
import DeleteModal from '@/components/modal/DeleteModal.vue'

export default {
  name: 'BankBrokersModal',
  components: { DeleteModal },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    bank: {
      type: Object,
      default: null
    },
    brokerList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedBrokerId: null,
      loadingAction: null,
      confirmDeleteDialog: false,
      pendingRemoveBroker: null
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    // Set of broker IDs currently linked to this bank
    linkedBrokerIdSet() {
      if (!this.bank || !Array.isArray(this.bank.raw_brokerlist || this.bank.brokerlist)) {
        return new Set()
      }
      const list = this.bank.raw_brokerlist || this.bank.brokerlist
      return new Set(list.map(b => (typeof b === 'object' && b ? b.id : b)))
    },
    // Enriched list of currently linked brokers with hierarchy info
    linkedBrokers() {
      if (!this.bank) return []
      const list = this.bank.brokerlist || []
      
      return list.map(item => {
        const fullBroker = this.brokerList.find(b => b.id === item.id) || item
        const isPartner = !!(fullBroker.partmaster_id && fullBroker.partmaster_id !== 0 && fullBroker.partmaster_id !== '0')
        const masterBroker = isPartner ? this.brokerList.find(b => b.id === fullBroker.partmaster_id) : null
        
        // Count partners of this master that are also linked to this bank
        let linkedPartnersCount = 0
        if (!isPartner) {
          linkedPartnersCount = this.brokerList.filter(
            b => b.partmaster_id === fullBroker.id && this.linkedBrokerIdSet.has(b.id)
          ).length
        }

        return {
          id: item.id,
          name: fullBroker.name || item.name || item.id,
          cnpj: fullBroker.cnpj || item.cnpj,
          email: fullBroker.email || item.email,
          is_partner: isPartner,
          master_name: masterBroker?.name || 'Unknown',
          linked_partners_count: linkedPartnersCount,
          raw: fullBroker
        }
      }).sort((a, b) => a.name.localeCompare(b.name))
    },
    // Available brokers for the add dropdown with partner constraint checks
    availableBrokerOptions() {
      return this.brokerList
        .filter(broker => !this.linkedBrokerIdSet.has(broker.id))
        .map(broker => {
          const isPartner = !!(broker.partmaster_id && broker.partmaster_id !== 0 && broker.partmaster_id !== '0')
          const master = isPartner ? this.brokerList.find(b => b.id === broker.partmaster_id) : null
          const isMasterLinked = isPartner && master ? this.linkedBrokerIdSet.has(master.id) : true
          
          let disabled = false
          let disabledReason = ''
          if (isPartner && !isMasterLinked) {
            disabled = true
            disabledReason = `Broker (${master?.name || 'Unknown'}) must be linked first`
          }

          return {
            id: broker.id,
            name: broker.name,
            displayName: isPartner ? `${broker.name} (Partner)` : broker.name,
            isPartner,
            disabled,
            disabledReason
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    isSelectedDisabled() {
      const selected = this.availableBrokerOptions.find(o => o.id === this.selectedBrokerId)
      return selected ? selected.disabled : false
    },
    // Linked partner brokers that would cascade-unlink if the selected master is removed
    pendingRemoveDependentPartners() {
      if (!this.pendingRemoveBroker || this.pendingRemoveBroker.is_partner) {
        return []
      }
      const masterId = this.pendingRemoveBroker.id
      return this.brokerList.filter(
        b => b.partmaster_id === masterId && this.linkedBrokerIdSet.has(b.id)
      )
    }
  },
  methods: {
    close() {
      if (!this.loading) {
        this.internalValue = false
        this.selectedBrokerId = null
      }
    },
    handleAddBroker() {
      if (!this.selectedBrokerId || this.isSelectedDisabled) return
      this.loadingAction = 'add'
      this.$emit('add-broker', {
        bank: this.bank,
        brokerId: this.selectedBrokerId,
        onComplete: () => {
          this.loadingAction = null
          this.selectedBrokerId = null
        }
      })
    },
    requestRemoveBroker(broker) {
      this.pendingRemoveBroker = broker
      this.confirmDeleteDialog = true
    },
    confirmRemoveBroker() {
      if (!this.pendingRemoveBroker) return
      this.loadingAction = 'remove'
      const brokerId = this.pendingRemoveBroker.id
      this.$emit('remove-broker', {
        bank: this.bank,
        brokerId,
        onComplete: () => {
          this.loadingAction = null
          this.confirmDeleteDialog = false
          this.pendingRemoveBroker = null
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .v-select.select-broker-dropdown .v-select__selections,
::v-deep .v-select.select-broker-dropdown .v-select__selection {
  font-size: 1.05rem !important;
}
::v-deep .v-select.select-broker-dropdown .v-label {
  font-size: 1rem !important;
}
</style>
